<template>
    <b-modal
        :id="modalId"
        v-model="visible"
        title="Discontinue Equivalent Prescriptions"
        size="xl"
        hide-footer
        no-close-on-backdrop
        @show="onShow"
        @hidden="onHidden"
    >
        <b-alert show variant="info" class="mb-3">
            <i class="fas fa-info-circle"></i>
            The prescription you are saving has equivalent drugs with existing patient prescriptions.
            Select which prescriptions you would like to discontinue.
        </b-alert>

        <b-overlay :show="loading" rounded="sm">
            <!-- Equivalent Prescriptions Table -->
            <div v-if="equivalentPrescriptions.length > 0">
                <h6 class="mb-2">Found {{ equivalentPrescriptions.length }} Equivalent Prescription(s)</h6>

                <b-table
                    :items="equivalentPrescriptions"
                    :fields="tableFields"
                    :select-mode="'multi'"
                    selectable
                    striped
                    hover
                    responsive
                    @row-selected="onRowSelected"
                    ref="prescriptionsTable"
                >
                    <!-- Rx Number with link -->
                    <template #cell(rxIdentifier)="data">
                        <router-link
                            :to="{
                                name: 'PrescriptionDetails',
                                params: {
                                    storeID: data.item.storeID,
                                    rxNumber: data.item.rxNumber,
                                    rfNumber: data.item.rfNumber
                                }
                            }"
                            target="_blank"
                        >
                            {{ data.item.storeID }}-{{ data.item.rxNumber }}-{{ data.item.rfNumber }}
                        </router-link>
                    </template>

                    <!-- Drug Name -->
                    <template #cell(drugName)="data">
                        <div>
                            <strong>{{ data.item.drugName }}</strong>
                            <br>
                            <small class="text-muted">NDC: {{ data.item.ndc }}</small>
                        </div>
                    </template>

                    <!-- Equivalence Reason Badge -->
                    <template #cell(equivalenceReason)="data">
                        <b-badge :variant="data.item.equivalenceReason === 'Same Product' ? 'danger' : 'warning'">
                            {{ data.item.equivalenceReason }}
                        </b-badge>
                    </template>

                    <!-- Status -->
                    <template #cell(status)="data">
                        {{ getStatusName(data.item.status) }}
                    </template>

                    <!-- Written Date -->
                    <template #cell(writtenDate)="data">
                        {{ formatDate(data.item.writtenDate) }}
                    </template>

                    <!-- Prescriber -->
                    <template #cell(prescriberName)="data">
                        {{ data.item.prescriberName || 'N/A' }}
                    </template>
                </b-table>
            </div>

            <!-- No equivalents found -->
            <b-alert v-else show variant="success">
                <i class="fas fa-check-circle"></i>
                No equivalent prescriptions found. You can proceed with saving.
            </b-alert>
        </b-overlay>

        <!-- SubStatus Selection -->
        <div v-if="equivalentPrescriptions.length > 0" class="mt-3">
            <b-form-group
                label="Discontinuation Reason"
                label-for="substatus-select"
                description="Select the reason for discontinuing these prescriptions"
            >
                <b-form-select
                    id="substatus-select"
                    v-model="selectedSubStatusId"
                    :options="subStatusOptions"
                    :disabled="loading"
                >
                    <template #first>
                        <b-form-select-option :value="null">-- Select Reason --</b-form-select-option>
                    </template>
                </b-form-select>
            </b-form-group>
        </div>

        <!-- Action Buttons -->
        <div v-if="equivalentPrescriptions.length > 0" class="mt-2">
            <b-row>
                <b-col cols="6">
                    <b-button
                        block
                        variant="secondary"
                        @click="skip"
                        :disabled="processing"
                    >
                        Continue Without Discontinuing
                    </b-button>
                </b-col>
                <b-col cols="6">
                    <b-button
                        block
                        variant="danger"
                        @click="discontinueSelected"
                        :disabled="selectedRxIds.length === 0 || !selectedSubStatusId || processing"
                    >
                        <b-spinner small v-if="processing" class="mr-2"></b-spinner>
                        Discontinue Selected ({{ selectedRxIds.length }})
                    </b-button>
                </b-col>
            </b-row>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, VModel } from 'vue-property-decorator';
import Axios from 'axios';
import type { EquivalentPrescription, DiscontinueEquivalentRequest, DiscontinueEquivalentResponse, SubStatus } from '@/models/EquivalentPrescription';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: 'DiscontinueEquivalentScriptsModal',
})
export default class DiscontinueEquivalentScriptsModal extends Vue {
    @VModel({ type: Boolean, default: false }) visible!: boolean;

    @Prop({ type: String, default: 'discontinue-equivalent-modal' })
    private modalId!: string;

    @Prop({ type: Number, required: true })
    private patientId!: number;

    @Prop({ type: Number, required: true })
    private packageId!: number;

    @Prop({ type: Number })
    private currentStoreId?: number;

    @Prop({ type: Number })
    private currentRxNumber?: number;

    @Prop({ type: Array, default: () => [] })
    private initialEquivalentPrescriptions!: EquivalentPrescription[];

    // Data
    private equivalentPrescriptions: EquivalentPrescription[] = [];
    private selectedRxIds: string[] = [];
    private loading: boolean = false;
    private processing: boolean = false;
    private subStatuses: SubStatus[] = [];
    private selectedSubStatusId: number | null = null;
    // True once the operator resolved the prompt (discontinue or skip), so @hidden can
    // tell a resolution apart from a dismissal (X / esc) and emit "dismissed" only then.
    private resolved = false;

    // Table configuration
    private tableFields = [
        { key: 'rxIdentifier', label: 'Rx #', sortable: true },
        { key: 'drugName', label: 'Drug', sortable: true },
        { key: 'equivalenceReason', label: 'Equivalent Type', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'writtenDate', label: 'Written Date', sortable: true },
        { key: 'refillsRemaining', label: 'Refills Left', sortable: true },
        { key: 'prescriberName', label: 'Prescriber', sortable: true },
    ];

    // Computed
    get subStatusOptions() {
        return this.subStatuses.map(ss => ({
            value: ss.id,
            text: ss.name,
        }));
    }

    // Methods
    async onShow(): Promise<void> {
        this.resolved = false;
        // Use passed data if available, otherwise load from API
        if (this.initialEquivalentPrescriptions.length > 0) {
            this.equivalentPrescriptions = this.initialEquivalentPrescriptions;
            await this.loadSubStatuses();
        } else {
            await Promise.all([
                this.loadEquivalentPrescriptions(),
                this.loadSubStatuses(),
            ]);
        }
    }

    onHidden(): void {
        // b-modal @hidden fires for every close. Emit "dismissed" only when the operator
        // closed it without discontinuing or skipping, so the parent can cancel the pending
        // save attempt parked at this gate.
        if (!this.resolved) this.$emit('dismissed');
        this.reset();
    }

    async loadEquivalentPrescriptions(): Promise<void> {
        this.loading = true;
        try {
            const response = await Axios.get<EquivalentPrescription[]>(
                '/Prescription/EquivalentPrescriptions',
                {
                    params: {
                        patientId: this.patientId,
                        packageId: this.packageId,
                        storeId: this.currentStoreId,
                        rxNumber: this.currentRxNumber,
                    },
                }
            );

            this.equivalentPrescriptions = response.data;
        } catch (error) {
            console.error('Error loading equivalent prescriptions:', error);
            this.$notification(
                NotificationOptions.error('Failed to load equivalent prescriptions')
            );
        } finally {
            this.loading = false;
        }
    }

    async loadSubStatuses(): Promise<void> {
        try {
            const response = await Axios.get<SubStatus[]>(
                '/Prescription/DiscontinuedSubStatus'
            );
            this.subStatuses = response.data.filter(ss => ss.active);
        } catch (error) {
            console.error('Error loading sub-statuses:', error);
            this.$notification(
                NotificationOptions.error('Failed to load discontinuation reasons')
            );
        }
    }

    onRowSelected(items: EquivalentPrescription[]): void {
        this.selectedRxIds = items.map(item => item.rxId);
    }

    async discontinueSelected(): Promise<void> {
        if (this.selectedRxIds.length === 0 || !this.selectedSubStatusId) return;

        this.processing = true;
        try {
            const request: DiscontinueEquivalentRequest = {
                rxIds: this.selectedRxIds,
                subStatusId: this.selectedSubStatusId,
            };

            const response = await Axios.post<DiscontinueEquivalentResponse>(
                '/Prescription/DiscontinueEquivalent',
                request
            );

            if (response.data.errors && response.data.errors.length > 0) {
                this.$notification(
                    NotificationOptions.error(
                        `Errors occurred: ${response.data.errors.join(', ')}`
                    )
                );
            }

            if (response.data.discontinuedCount > 0) {
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        `${response.data.discontinuedCount} prescription(s) discontinued`
                    )
                );
            }

            this.resolved = true;
            this.$emit('discontinued', response.data);
            this.visible = false;
        } catch (error: any) {
            console.error('Error discontinuing prescriptions:', error);
            this.$notification(
                NotificationOptions.error(
                    error.response?.data || 'Failed to discontinue prescriptions'
                )
            );
        } finally {
            this.processing = false;
        }
    }

    skip(): void {
        this.resolved = true;
        this.$emit('skipped');
        this.visible = false;
    }

    reset(): void {
        this.equivalentPrescriptions = [];
        this.selectedRxIds = [];
        this.selectedSubStatusId = null;
        this.loading = false;
        this.processing = false;
    }

    getStatusName(status: number): string {
        const statusNames: Record<number, string> = {
            0: 'Refillable',
            1: 'Auto Refillable',
            2: 'Non Refillable',
            3: 'Suspended',
            4: 'Transfer Out',
            5: 'Transfer In',
            6: 'On Hold',
            7: 'Canceled',
            8: 'Expired',
            9: 'Void',
            10: 'Awaiting Insurance',
            11: 'Discontinued',
        };
        return statusNames[status] || 'Unknown';
    }

    formatDate(date?: string): string {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString();
    }
}
</script>

<style scoped>
.table >>> tbody tr {
    cursor: pointer;
}
</style>
